import { HeadingLevel, ImageRun } from 'docx'
import { Paragraph, Table, TableRow, TableCell, WidthType, TextRun } from 'docx'
import * as echarts from 'echarts/core'
import htmlToPdfmake from 'html-to-pdfmake'
import { marked } from 'marked'

import { HuntCoverage } from '@cyborg-monorepo/hunter/common'

export function convertInline(el: Element): TextRun[] {
  const runs: TextRun[] = []

  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      runs.push(new TextRun(node.textContent || ''))
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const child = node as HTMLElement
      const text = child.textContent || ''
      switch (child.tagName) {
        case 'STRONG':
        case 'B':
          runs.push(new TextRun({ text, bold: true }))
          break
        case 'EM':
        case 'I':
          runs.push(new TextRun({ text, italics: true }))
          break
        case 'A': {
          runs.push(
            new TextRun({
              text,
              style: 'Hyperlink',
              underline: {},
              color: '0000FF',
            }),
          )
          break
        }
        default:
          runs.push(new TextRun(text))
      }
    }
  }
  return runs
}

export function markdownToDocxParagraphs(markdown: string): Paragraph[] {
  const html = marked.parse(markdown)
  const doc = new DOMParser().parseFromString(html as string, 'text/html')
  const paragraphs: Paragraph[] = []

  for (const el of Array.from(doc.body.children)) {
    const tag = el.tagName
    if (tag === 'H1') {
      paragraphs.push(
        new Paragraph({
          text: el.textContent || '',
          heading: HeadingLevel.HEADING_1,
        }),
      )
    } else if (tag === 'H2') {
      paragraphs.push(
        new Paragraph({
          text: el.textContent || '',
          heading: HeadingLevel.HEADING_2,
        }),
      )
    } else if (tag === 'UL') {
      for (const li of el.querySelectorAll('li')) {
        paragraphs.push(
          new Paragraph({ children: convertInline(li), bullet: { level: 0 } }),
        )
      }
    } else if (tag === 'OL') {
      let cnt = 1
      for (const li of el.querySelectorAll('li')) {
        paragraphs.push(
          new Paragraph({
            children: [new TextRun(`${cnt}. `), ...convertInline(li)],
          }),
        )
        cnt++
      }
    } else {
      // P, DIV, or other tags
      paragraphs.push(new Paragraph({ children: convertInline(el) }))
    }
  }
  return paragraphs
}

export function buildImageParagraph(base64: string): Paragraph {
  const base64Data = base64.replace(/^data:image\/\w+;base64,/, '')
  const mimeMatch = base64.match(/^data:(image\/[a-zA-Z]+);base64,/)
  const imageType = mimeMatch ? mimeMatch[1].split('/')[1] : 'png' // fallback to png

  const imageBuffer = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0))

  return new Paragraph({
    children: [
      new ImageRun({
        data: imageBuffer,
        transformation: { width: 600, height: 400 },
        type: imageType as 'png' | 'jpg' | 'bmp' | 'gif', // explicitly cast to supported types
      }),
    ],
    alignment: 'center',
  })
}

/**
 * Recursively adds generated content (including nested pivots) to a pdfMake doc definition.
 */
export const renderContentToPdfMake = (
  content: any,
  parentTitle: string,
  docContent: any[],
) => {
  const title = content.title || content.category || 'Untitled'
  const fullTitle = parentTitle ? `${parentTitle} -> ${title}` : title

  docContent.push({
    text: fullTitle,
    style: 'subheader',
  })

  docContent.push({
    text: `Query:\n${content.query}`,
    style: 'text',
  })

  const notesContent = markdownToPdfMake(content.notes || '')
  if (Array.isArray(notesContent)) {
    docContent.push(...notesContent)
  } else {
    docContent.push(notesContent)
  }

  // Recurse through child pivots if any
  if (Array.isArray(content.pivots) && content.pivots.length) {
    for (const pivot of content.pivots) {
      renderContentToPdfMake(pivot, fullTitle, docContent)
    }
  }
}

/**
 * Converts a Markdown string to pdfMake-compatible format.
 */
export const markdownToPdfMake = (markdownText: string) => {
  const preprocessed = convertMarkdownLinksToHtml(markdownText)
  const html = marked.parse(preprocessed)
  return htmlToPdfmake(html as string)
}

/**
 * Converts Markdown-style links into HTML anchor tags.
 *
 * Supports both fully-qualified URLs (https://...) and common partial URLs (www...).
 * Ensures links open in a new tab and adds "https://" to URLs missing a protocol.
 *
 * @param markdown - The raw markdown string to transform.
 * @returns The string with Markdown links replaced by HTML <a> tags.
 */
export function convertMarkdownLinksToHtml(markdown: string): string {
  return markdown.replace(
    // Match Markdown links: [link text](https://example.com) or [link text](www.example.com)
    /\[([^\]]+)\]\(((https?:\/\/|www\.)[^\s)]+)\)/g,

    // Replacement function for each matched link
    (_match, text, url) => {
      // Ensure the URL starts with 'http' to make it a valid anchor href
      const href = url.startsWith('http') ? url : `https://${url}`

      // Return a valid HTML anchor tag with target="_blank" for opening in new tab
      return `<a href="${href}" target="_blank">${text}</a>`
    },
  )
}

/**
 * Adds a full markdown section (with a header) to a pdfMake doc definition.
 */
export const addMarkdownSection = (
  headerText: string,
  markdown: string,
  docDefinition: any,
) => {
  docDefinition.content.push({ text: headerText, style: 'header' })
  const pdfContent = markdownToPdfMake(markdown)
  if (Array.isArray(pdfContent)) {
    docDefinition.content.push(...pdfContent)
  } else {
    docDefinition.content.push(pdfContent)
  }
}

export function getChartImageBase64(
  chartInstance: echarts.ECharts,
): string | null {
  const canvas = chartInstance?.getDom()?.getElementsByTagName('canvas')[0]
  if (canvas) {
    const base64 = canvas.toDataURL('image/png')
    return base64 && base64 !== 'data:,' ? base64 : null
  }
  return null
}

export function buildMitreTableDocx(coverage: HuntCoverage[]): Table {
  const createCell = (text: string): TableCell =>
    new TableCell({
      children: [new Paragraph(text)],
    })

  const headerRow = new TableRow({
    tableHeader: true,
    children: [
      createCell('Technique'),
      createCell('Tactic'),
      createCell('Packages'),
    ],
  })

  const dataRows = coverage.map(
    (mitre) =>
      new TableRow({
        children: [
          createCell(mitre.name),
          createCell(mitre.category),
          createCell(String(mitre.huntPackageCount)),
        ],
      }),
  )

  return new Table({
    width: {
      size: 9600, // DXA units
      type: WidthType.DXA,
    },
    columnWidths: [3200, 3200, 3200],
    rows: [headerRow, ...dataRows],
  })
}

export function buildActorTableDocx(coverage: HuntCoverage[]): Table {
  const createCell = (text: string): TableCell =>
    new TableCell({
      children: [new Paragraph(text)],
    })

  const headerRow = new TableRow({
    tableHeader: true,
    children: [
      createCell('Name'),
      createCell('Origin'),
      createCell('Packages'),
    ],
  })

  const dataRows = coverage.map(
    (actor) =>
      new TableRow({
        children: [
          createCell(actor.name),
          createCell(actor.category),
          createCell(String(actor.huntPackageCount)),
        ],
      }),
  )

  return new Table({
    width: {
      size: 9600, // DXA units
      type: WidthType.DXA,
    },
    columnWidths: [3200, 3200, 3200],
    rows: [headerRow, ...dataRows],
  })
}

export function buildThreatTableDocx(coverage: HuntCoverage[]): Table {
  const createCell = (text: string): TableCell =>
    new TableCell({
      children: [new Paragraph(text)],
    })

  const headerRow = new TableRow({
    tableHeader: true,
    children: [createCell('Name'), createCell('Packages')],
  })

  const dataRows = coverage.map(
    (threat) =>
      new TableRow({
        children: [
          createCell(threat.name),
          createCell(String(threat.huntPackageCount)),
        ],
      }),
  )

  return new Table({
    width: {
      size: 9600, // DXA units
      type: WidthType.DXA,
    },
    columnWidths: [3200, 3200, 3200],
    rows: [headerRow, ...dataRows],
  })
}

// Recursively renders Hunt content into docx Paragraphs
export const renderContentToDocx = (
  content: any,
  parentTitle = '',
): Paragraph[] => {
  const paragraphs: Paragraph[] = []

  const title = content.title || content.category || 'Untitled'
  const fullTitle = parentTitle ? `${parentTitle} ➝ ${title}` : title

  paragraphs.push(new Paragraph({ spacing: { before: 100 } }))

  // Section Header
  paragraphs.push(
    new Paragraph({
      text: fullTitle,
      heading: 'Heading4',
    }),
  )

  // Add Query label
  if (content.query) {
    paragraphs.push(
      new Paragraph({
        text: 'Query:',
        heading: 'Heading5',
      }),
    )

    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: content.query,
            font: 'Courier New',
            size: 20,
          }),
        ],
        spacing: { after: 200 },
      }),
    )
  }

  // Add Notes label
  if (content.notes) {
    paragraphs.push(
      new Paragraph({
        text: 'Notes:',
        heading: 'Heading5',
      }),
    )

    const notesParagraphs = markdownToDocxParagraphs(content.notes)
    paragraphs.push(...notesParagraphs)
  }

  // Handle pivots recursively
  if (Array.isArray(content.pivots) && content.pivots.length) {
    for (const pivot of content.pivots) {
      const pivotParagraphs = renderContentToDocx(pivot, fullTitle)
      paragraphs.push(...pivotParagraphs)
    }
  }

  return paragraphs
}

export function createHeading(
  text: string,
  level: (typeof HeadingLevel)[keyof typeof HeadingLevel],
  spacingBefore = 300,
): Paragraph {
  return new Paragraph({
    text,
    heading: level,
    spacing: { before: spacingBefore },
  })
}

export function getBase64FromImageUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')
    img.onload = function () {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx?.drawImage(img, 0, 0)
      const dataURL = canvas.toDataURL('image/png')
      resolve(dataURL)
    }
    img.onerror = reject
    img.src = url
  })
}
